import React from "react";
import { Mail, MapPin, Phone } from "lucide-react";
import { formatPhone } from "./utils";

// Static accent classes — enumerated so Tailwind's purger can include them.
export const ACCENTS = {
  indigo:  { bg: "bg-indigo-50",  text: "text-indigo-700",  ring: "ring-indigo-200",  badge: "bg-indigo-50 text-indigo-700 ring-indigo-200" },
  purple:  { bg: "bg-purple-50",  text: "text-purple-700",  ring: "ring-purple-200",  badge: "bg-purple-50 text-purple-700 ring-purple-200" },
  rose:    { bg: "bg-rose-50",    text: "text-rose-700",    ring: "ring-rose-200",    badge: "bg-rose-50 text-rose-700 ring-rose-200" },
  amber:   { bg: "bg-amber-50",   text: "text-amber-700",   ring: "ring-amber-200",   badge: "bg-amber-50 text-amber-700 ring-amber-200" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", badge: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  slate:   { bg: "bg-slate-50",   text: "text-slate-700",   ring: "ring-slate-200",   badge: "bg-slate-50 text-slate-700 ring-slate-200" },
  sky:     { bg: "bg-sky-50",     text: "text-sky-700",     ring: "ring-sky-200",     badge: "bg-sky-50 text-sky-700 ring-sky-200" },
};

export function PartyCard({ icon: Icon, title, subtitle, name, phones = [], emails = [], address, accent = "indigo", testId }) {
  const hasAny = name || phones?.length || emails?.length || address;
  if (!hasAny) return null;
  const a = ACCENTS[accent] || ACCENTS.indigo;

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-5 transition-shadow hover:shadow-sm" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`h-10 w-10 rounded-lg ${a.bg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`h-4 w-4 ${a.text}`} strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <p className={`text-[10px] font-bold uppercase tracking-widest ${a.text}`}>{title}</p>
            <p
              className="text-base font-black text-zinc-950 tracking-tight truncate"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              data-testid={`${testId}-name`}
            >
              {name || <span className="text-zinc-400 font-semibold">Not available</span>}
            </p>
          </div>
        </div>
        {subtitle && (
          <span className={`inline-flex items-center rounded-md ring-1 ${a.badge} px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest whitespace-nowrap`}>
            {subtitle}
          </span>
        )}
      </div>
      {(phones?.length > 0 || emails?.length > 0) && (
        <div className="space-y-2">
          {phones.map((p, i) => {
            const digits = String(p).replace(/\D/g, "");
            return (
              <a
                key={`ph-${i}`}
                href={`tel:${digits}`}
                className="flex items-center gap-2.5 rounded-md border border-zinc-200 bg-white px-3 py-2 hover:border-green-500 hover:bg-green-50 transition-colors"
                data-testid={`${testId}-phone-${i}`}
              >
                <Phone className="h-3.5 w-3.5 text-green-700 flex-shrink-0" strokeWidth={2.5} />
                <span className="font-mono text-sm font-bold text-zinc-950">{formatPhone(p)}</span>
              </a>
            );
          })}
          {emails.map((e, i) => (
            <a
              key={`em-${i}`}
              href={`mailto:${e}`}
              className="flex items-center gap-2.5 rounded-md border border-zinc-200 bg-white px-3 py-2 hover:border-blue-500 hover:bg-blue-50 transition-colors"
              data-testid={`${testId}-email-${i}`}
            >
              <Mail className="h-3.5 w-3.5 text-blue-700 flex-shrink-0" strokeWidth={2.5} />
              <span className="text-sm font-bold text-zinc-950 truncate">{e}</span>
            </a>
          ))}
        </div>
      )}
      {address && (
        <div className="flex items-start gap-1.5 mt-3 text-xs text-zinc-600">
          <MapPin className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-zinc-400" />
          <span className="break-words">{address}</span>
        </div>
      )}
    </div>
  );
}

export function Stat({ label, value, accent }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">{label}</p>
      <p
        className={`text-2xl font-black tracking-tight tabular-nums ${accent || "text-zinc-950"}`}
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        {value ?? "—"}
      </p>
    </div>
  );
}

export function SectionTitle({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className="h-7 w-7 rounded-md bg-zinc-950 flex items-center justify-center">
        <Icon className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
      </div>
      <h3
        className="text-xs font-black uppercase tracking-widest text-zinc-950"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        {title}
      </h3>
    </div>
  );
}

export function InfoRow({ label, value, testId }) {
  return (
    <div className="py-2.5 border-b border-zinc-100 last:border-b-0 grid grid-cols-[130px_1fr] gap-4" data-testid={testId}>
      <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="text-sm text-zinc-900 break-words">{value || <span className="text-zinc-300">—</span>}</p>
    </div>
  );
}
